import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// One-time migration on module load: scrub the old localStorage entry
// from v1 (refresh token used to live there). Without this, an existing
// user upgrading would still have the leaked-key window we just closed.
if (typeof window !== "undefined" && window.localStorage) {
  try {
    window.localStorage.removeItem("kraetdnet-auth");
  } catch {
    // Private mode / disabled storage — fine, nothing to clean.
  }
}

export type User = {
  id: string;
  email: string;
  name: string;
  mfa_enabled?: boolean;
};

export type Tenant = {
  id: string;
  name: string;
  tier: string;
  pc_limit: number;
  status: string;
  trial_ends_at: string | null;
  billing_exempt: boolean;
  // Optional: stale tokens from before the office_code migration ran
  // won't have this field until the user's next /me refresh. Callers
  // must guard with `tenant?.office_code` to avoid a runtime crash.
  office_code?: string;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  mfaRequired: boolean;
  user: User | null;
  tenant: Tenant | null;
  role: string | null;
  // Set when the API returned 402 (trial expired). Cleared on a fresh
  // profile load, which is how we recover after subscription. Not
  // persisted across sessions — re-derived from the next 402, or stays
  // false if the tenant has paid in the meantime.
  trialExpired: boolean;
  setSession: (args: {
    accessToken: string;
    refreshToken: string;
    mfaRequired: boolean;
    user?: User | null;
    tenant?: Tenant | null;
    role?: string | null;
  }) => void;
  setProfile: (args: { user: User; tenant: Tenant; role: string }) => void;
  setTokens: (args: { accessToken: string; refreshToken: string; mfaRequired: boolean }) => void;
  setTrialExpired: (v: boolean) => void;
  clear: () => void;
};

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      mfaRequired: false,
      user: null,
      tenant: null,
      role: null,
      trialExpired: false,
      setSession: (args) =>
        set({
          accessToken: args.accessToken,
          refreshToken: args.refreshToken,
          mfaRequired: args.mfaRequired,
          user: args.user ?? null,
          tenant: args.tenant ?? null,
          role: args.role ?? null,
        }),
      // Reset trialExpired on a fresh profile load. The tenant payload
      // tells us the current status — if they've paid, the 402 flag we
      // set earlier is now stale.
      setProfile: ({ user, tenant, role }) =>
        set({
          user,
          tenant,
          role,
          trialExpired:
            tenant.status === "trial" &&
            tenant.trial_ends_at !== null &&
            new Date(tenant.trial_ends_at) < new Date() &&
            !tenant.billing_exempt,
        }),
      setTokens: ({ accessToken, refreshToken, mfaRequired }) =>
        set({ accessToken, refreshToken, mfaRequired }),
      setTrialExpired: (trialExpired) => set({ trialExpired }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          mfaRequired: false,
          user: null,
          tenant: null,
          role: null,
          trialExpired: false,
        }),
    }),
    {
      name: "kraetdnet-auth",
      // v1: accessToken removed from storage — memory only to limit XSS exposure.
      // v2: switched to sessionStorage. An XSS payload that exfiltrates the
      // refresh token now only owns the user for as long as the browser
      // tab stays open — closing the browser invalidates the leaked token
      // (well, server-side TTL is still 14 days, but no new sessions can
      // pick it back up). Trade-off: closing the tab logs the user out.
      // We deliberately accept that for the security win.
      storage: createJSONStorage(() => sessionStorage),
      version: 2,
      migrate: (persisted: unknown, version: number) => {
        if (version === 0) {
          const old = persisted as Record<string, unknown>;
          delete old.accessToken;
          return old;
        }
        // v1 → v2: data was in localStorage. Don't auto-migrate — force
        // a fresh login so the leaked-key window closes. Returning an
        // empty state is safe; PrivateRoute will redirect to /login.
        return persisted as Partial<AuthState>;
      },
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        mfaRequired: state.mfaRequired,
        user: state.user,
        tenant: state.tenant,
        role: state.role,
      }),
    },
  ),
);
